import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import LoadingButton from '@mui/lab/LoadingButton';
import {
	Alert, Box, Button, CardActions, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, FormControlLabel,
	FormHelperText, Grid, Link, MenuItem, TextField, Tooltip, Typography
} from '@mui/material';
import { FormikProps } from 'formik';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { PhoneInput } from '../../../../components/PhoneInput/PhoneInput';
import { api } from '../../../../services';

interface Props {
	onComplete: () => void;
	formik: FormikProps<any>;
	ipcPositions: string[];
	onboardJWT: string;
	onboardId: string;
	getOnboardJWT: (_email: string) => Promise<any>;
}

export const IPCStep1: FC<Props> = memo(({ onComplete, formik, ipcPositions, onboardJWT: _onboardJWT, onboardId, getOnboardJWT }) => {
	const { enqueueSnackbar } = useSnackbar();
	const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string;

	const [loading, setLoading] = useState<boolean>(false);
	const [hasError, setHasError] = useState<boolean>(false);
	const [locked, setLocked] = useState<boolean>(false);
	const [showDuplicateEmailAlert, setShowDuplicateEmailAlert] = useState<boolean>(false);
	const [showLinkAccountDialog, setShowLinkAccountDialog] = useState<boolean>(false);
	const [showAccountLinkedAlert, setShowAccountLinkedAlert] = useState<boolean>(false);


	const onReCaptchaChange = async (token: string | null) => {
		await formik.setFieldValue('reCaptchaToken', token);
		setTimeout(() => { formik.validateForm(); }, 100);
	};

	const onValidateEmail = async (blurEvent?: any, emailValue?: string) => {
		setLoading(true);
		if (blurEvent) {
			formik.handleBlur(blurEvent);
		}
		const email = emailValue ? emailValue : formik.values.email.trim();
		if (email.length === 0) {
			return setLoading(false);
		}
		try {
			// Get onboardJWT if we don't have it yet
			let currentOnboardId = onboardId;
			if (!currentOnboardId) {
				const onboardRes = await getOnboardJWT(email);
				currentOnboardId = onboardRes._id;
			}

			const { exists } = await api.get.checkEmail(currentOnboardId, email);
			setShowDuplicateEmailAlert(exists);
			if (exists) {
				setShowLinkAccountDialog(true);
			} else {
				await formik.setFieldValue('isMultiStoreOwner', false);
			}
			return setLoading(false);
		} catch (_error) {
			enqueueSnackbar(
				'Something went wrong while verifying your email address.',
				{ variant: 'error' },
			);
			return setLoading(false);
		}
	};

	const debouncedOnEmailChange = useCallback(debounce((event) => {
		const { target: { value, } } = event;
		const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
		const isValidEmail = emailRegex.test(value);
		if (isValidEmail) {
			onValidateEmail(undefined, value);
		}
	}, 1500, { leading: false, trailing: true, }), []);

	const onEmailChange = (event: any) => {
		formik.handleChange(event);
		debouncedOnEmailChange(event);
	};

	const onLinkExistingAccount = async () => {
		const { values: { email, password } } = formik;
		if (!password || password.trim().length === 0) {
			return;
		}
		setLoading(true);
		setHasError(false);
		try {
			const { authenticated } = await api.get.checkPassword(email, password);
			if (authenticated) {
				await formik.setFieldValue('isMultiStoreOwner', true);
				setShowDuplicateEmailAlert(false);
				setShowAccountLinkedAlert(true);
				setShowLinkAccountDialog(false);
			} else {
				await formik.setFieldValue('isMultiStoreOwner', false);
				setHasError(true);
			}
			setLoading(false);
		} catch (error) {
			if ((error as any).status === 423) {
				setHasError(false);
				setLocked(true);
			}
			enqueueSnackbar(
				'Something went wrong while linking your existing account.',
				{ variant: 'error' },
			);
			setLoading(false);
		}
		await formik.setFieldValue('password', '');
	};

	const handleCloseLinkAccountDialog = () => {
		setShowLinkAccountDialog(false);
		setShowAccountLinkedAlert(false);
		setLocked(false);
	};

	// Check if any step 1 fields have errors
	const hasStep1Errors =
		formik.errors.firstName ||
		formik.errors.lastName ||
		formik.errors.position ||
		formik.errors.npiNumber ||
		formik.errors.phone ||
		formik.errors.email ||
		(!formik.values.isMultiStoreOwner && formik.errors.password) ||
		(!formik.values.isMultiStoreOwner && formik.errors.confirmPassword) ||
		formik.errors.termsAccepted ||
		formik.errors.reCaptchaToken;

	const disabled = formik.isSubmitting || Boolean(hasStep1Errors) || !formik.values.reCaptchaToken || loading || showDuplicateEmailAlert;

	return (
		<Box>
			<Dialog open={showLinkAccountDialog} onClose={handleCloseLinkAccountDialog}>
				<DialogTitle>Connect Your Existing Account</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ mb: 2 }}>
						We found an existing account under the email address <b>{formik.values.email}</b>. Please enter your password to securely link your account to this new pharmacy.
					</DialogContentText>
					<DialogContentText sx={{ mb: 1 }}>
						If you haven&apos;t setup a password yet, please call <b>1 (800) 958-5540</b> or email <b>support@pharmacymarketplace.com</b> for assistance.
					</DialogContentText>
					{hasError
						&& <Alert
							sx={{
								ml: 'auto', mr: 'auto', mt: 1, mb: 1,
							}}
							style={{ fontWeight: 'bold' }}
							variant='filled'
							severity='error'
						>
							Incorrect password.
						</Alert>
					}
					{locked
						&& <Alert
							sx={{
								ml: 'auto', mr: 'auto', mt: 1, mb: 1,
							}}
							style={{ fontWeight: 'bold' }}
							variant='filled'
							severity='warning'
						>
							You&apos;ve exceeded the allowed number of failed login attempts (5). Your account has been locked for security.
							<br /><br />Please call <b>1 (800) 958-5540</b> or email <b>support@pharmacymarketplace.com</b> for assistance.
						</Alert>
					}
					<TextField
						autoFocus
						fullWidth
						sx={{ mt: 2 }}
						required={showLinkAccountDialog}
						id='password'
						label='Account Password'
						type='password'
						variant='outlined'
						value={formik.values.password}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.password && Boolean(formik.errors.password)}
						helperText={(formik.touched.password && formik.errors.password as string) || ' '}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseLinkAccountDialog} color='inherit'>
						Cancel
					</Button>
					<LoadingButton
						disabled={loading}
						loading={loading}
						onClick={onLinkExistingAccount}
						variant='contained'
					>
						Link Account
					</LoadingButton>
				</DialogActions>
			</Dialog>
			<CardContent>
				<Typography variant="h5" gutterBottom textAlign='center'>
					Welcome to the Pharmacy Marketplace!
				</Typography>
				<Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
					We&apos;re thrilled to serve you! Please complete the following application, which asks for basic information and will take just a few minutes.<br />
					The following information will be used to create the first User for your Pharmacy Marketplace account. We recommend providing the pharmacy owner&apos;s information, so they receive the initial emails. Additional users can be added from Settings once the account is set up.
				</Typography>
				<Grid container spacing={2}>
					{showAccountLinkedAlert && (
						<Grid item xs={12} sx={{ mb: 1 }}>
							<Alert
								sx={{ ml: 'auto', mr: 'auto' }}
								style={{ fontWeight: 'bold' }}
								variant='filled'
								severity='success'
							>
								{`Your account under the email address ${formik.values.email} has been linked successfully!`}
							</Alert>
						</Grid>
					)}
					{showDuplicateEmailAlert && !showLinkAccountDialog && (
						<Grid item xs={12} sx={{ mb: 1 }}>
							<Alert
								sx={{ ml: 'auto', mr: 'auto', color: '#fff', }}
								style={{ fontWeight: 'bold' }}
								variant='filled'
								severity='warning'
							>
								{`The email address ${formik.values.email} is already in use. Please pick a different one.`}
							</Alert>
						</Grid>
					)}
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							required
							name='firstName'
							label='First Name'
							value={formik.values.firstName}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.firstName && Boolean(formik.errors.firstName)}
							helperText={(formik.touched.firstName && formik.errors.firstName as string) || ' '}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							required
							name='lastName'
							label='Last Name'
							value={formik.values.lastName}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.lastName && Boolean(formik.errors.lastName)}
							helperText={(formik.touched.lastName && formik.errors.lastName as string) || ' '}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							select
							required
							name='position'
							label='Position'
							value={formik.values.position}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.position && Boolean(formik.errors.position)}
							helperText={(formik.touched.position && formik.errors.position as string) || ' '}
						>
							{ipcPositions.map((label) => (
								<MenuItem key={label} value={label}>
									{label}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							required
							name='npiNumber'
							label='NPI Number'
							type='number'
							value={formik.values.npiNumber}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.npiNumber && Boolean(formik.errors.npiNumber)}
							helperText={(formik.touched.npiNumber && formik.errors.npiNumber as string) || ' '}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							required
							name='email'
							label={loading ? 'Checking Email' : 'Email Address'}
							type='email'
							disabled={loading}
							value={formik.values.email}
							onChange={onEmailChange}
							onBlur={onValidateEmail}
							error={formik.touched.email && Boolean(formik.errors.email)}
							helperText={(formik.touched.email && formik.errors.email as string) || ' '}
							InputProps={{
								startAdornment: (
									<Tooltip title="You'll login to your account using this email address">
										<InfoIcon fontSize='small' sx={{ color: '#757575' }} />
									</Tooltip>
								),
							}}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							required
							name='phone'
							label='Mobile Phone Number'
							placeholder='(123) 456-7890'
							value={formik.values.phone}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.phone && Boolean(formik.errors.phone)}
							helperText={(formik.touched.phone && formik.errors.phone as string) || ' '}
							InputProps={{
								inputComponent: PhoneInput as any,
							}}
						/>
					</Grid>
					{!formik.values.isMultiStoreOwner && (
						<>
							<Grid item md={6} xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formik.values.emailEnabled}
											name="emailEnabled"
											onChange={formik.handleChange}
											color="primary"
										/>
									}
									label={
										<>
											<Typography variant="body2">
												Enable Email Notifications?
											</Typography>
											<FormHelperText>
												Sign up for emails about order updates and important purchasing opportunities.
												This can be changed in Settings once the account is set up.
											</FormHelperText>
										</>
									}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formik.values.pushEnabled}
											name="pushEnabled"
											onChange={formik.handleChange}
											color="primary"
										/>
									}
									label={
										<>
											<Typography variant="body2">
												Enable Push Notifications?
											</Typography>
											<FormHelperText>
												Sign up for mobile notifications about important purchasing opportunities
												if the app is downloaded on the device. This can be changed in Settings once the account is set up.
											</FormHelperText>
										</>
									}
								/>
							</Grid>
						</>
					)}
					{!formik.values.isMultiStoreOwner && (
						<Grid item xs={12}>
							<Divider sx={{ marginBottom: 1 }} />
							<Typography variant='subtitle1' color='textSecondary' sx={{ mt: 2, mb: 2 }}>
								Enter your desired password below. Your password <b>must</b>
								&nbsp;be at least 8 characters. We recommend including an uppercase, digit, and/or special character.
							</Typography>
							<Grid container spacing={2}>
								<Grid item md={6} xs={12}>
									<TextField
										label="Password"
										type="password"
										variant="outlined"
										name='password'
										required
										value={formik.values.password}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										error={formik.touched.password && Boolean(formik.errors.password)}
										helperText={(formik.touched.password && formik.errors.password as string) || ' '}
										fullWidth
									/>
								</Grid>
								<Grid item md={6} xs={12}>
									<TextField
										label="Confirm Password"
										type="password"
										variant="outlined"
										name='confirmPassword'
										required
										value={formik.values.confirmPassword}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
										helperText={formik.touched.confirmPassword && formik.errors.confirmPassword as string}
										fullWidth
									/>
								</Grid>
							</Grid>
						</Grid>
					)}
				</Grid>
			</CardContent>
			<Divider sx={{ marginBottom: 1 }} />
			<CardActions>
				<Grid
					container
					spacing={2}
					alignItems='center'
					sx={{ px: 1 }}
				>
					<Grid item xs={12} md={6}>
						<FormControl error={formik.touched.termsAccepted && Boolean(formik.errors.termsAccepted)}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formik.values.termsAccepted}
										name="termsAccepted"
										onChange={formik.handleChange}
										color="primary"
									/>
								}
								label={
									<Typography variant="body2">
										I acknowledge that I have read, understand, and agree to the{' '}
										<Link href="https://pharmacymarketplace.com/terms" target="_blank">
											Terms of Use
										</Link>{' '}
										and{' '}
										<Link href="https://pharmacymarketplace.com/privacy-policy" target="_blank">
											Privacy Policy
										</Link>
										.{' '}
										I consent to the collection, use, and processing of my data as described in the Privacy Policy.
									</Typography>
								}
							/>
							{formik.touched.termsAccepted && formik.errors.termsAccepted && (
								<FormHelperText>{formik.errors.termsAccepted as string}</FormHelperText>
							)}
						</FormControl>
					</Grid>
					<Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
						<div id="recaptcha">
							<ReCAPTCHA sitekey={siteKey} onChange={onReCaptchaChange} />
						</div>
					</Grid>
					<Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<LoadingButton
							type='submit'
							loading={formik.isSubmitting}
							loadingPosition='start'
							color={disabled ? 'inherit' : 'primary'}
							variant='contained'
							disabled={disabled}
							startIcon={<SendIcon />}
							onClick={onComplete}
						>
							Next
						</LoadingButton>
					</Grid>
				</Grid>
			</CardActions>
		</Box>
	);
}); 